import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { isHalfPairCustomDataRow } from 'libs.consent.common';
import { Button, CalloutNotification, Card, Checkbox, Icon, Input, RadioGroup, RadioGroupOption, Select, useLocalizeMessage, } from 'libs.nucleus';
import { ConsentConfigurationData } from './consent_configuration_data.component';
import { buildChoiceOptions, CONSENT_ELEMENT_TYPES, resequenceChoiceOptions, YN_NA_OPTION_SPEC, YN_DEFAULT_OPTION_SPECS, } from '../../constants/consent_element_types.constants';
import { useConsentActionsContext, useConsentStateContext } from '../../hooks/use_consent_context.hook';
import { useDocumentSignerRoles } from '../../hooks/use_consent_signer_roles.hook';
import { generateUniqueId } from '../../utils/generate_unique_id';
const NA_KEY = YN_NA_OPTION_SPEC.key;
/**
 * An option's Page field: held in local state while it is being edited, and written through only on
 * commit — blur or Enter.
 *
 * Clamping every `change` event instead, which is what this did, stored a page the author never
 * asked for. Typing `3` into a field reading `1` makes `13`, which clamped to the last page and was
 * written on that keystroke; and the empty string an author clears the field to is unparseable, so
 * the guard returned without writing, nothing re-rendered, and React restored the previous value —
 * taking clear-and-retype away as the recovery. Nothing downstream catches the result, because it is
 * a *valid* page: `placed` stays true and publish validation passes. The only cue is the marker
 * vanishing to a page the author is not looking at, and they cannot drag it back, since crossing
 * pages is the one thing this field exists to do.
 */
const ChoiceOptionPageField = ({ id, label, page, totalPages, onCommit, dataTestId, }) => {
    const [draft, setDraft] = useState(() => String(page));
    // The stored page can change under the field without the field remounting — an undo, or any edit
    // elsewhere in the panel that re-renders this row, which is keyed by option id and so survives.
    // Adjusted during render rather than from an effect so the field never paints the stale page
    // first.
    const [syncedPage, setSyncedPage] = useState(page);
    if (page !== syncedPage) {
        setSyncedPage(page);
        setDraft(String(page));
    }
    // Clamped rather than refused, and never written as anything but an integer in range: the overlay
    // renders an option only on the page whose number its `page` matches, so a value outside
    // 1..totalPages would place the option on no page at all — invisible on the document, and out of
    // reach of the marker controls that would otherwise bring it back. An empty field reverts to the
    // stored page rather than writing anything, because an option always sits on some page.
    const commit = () => {
        const requested = Number.parseInt(draft, 10);
        if (Number.isNaN(requested)) {
            setDraft(String(page));
            return;
        }
        const next = Math.min(totalPages, Math.max(1, requested));
        // Resynced even when there is nothing to write, so an out-of-range entry that clamps back onto
        // the page already stored still shows the page it actually landed on.
        setDraft(String(next));
        if (next !== page) {
            onCommit(next);
        }
    };
    // Set by focus and consumed by the click that follows it, so only the click that *entered* the
    // field selects what is in it — see the two handlers below.
    const selectOnClickRef = useRef(false);
    return (_jsx(Input, { id: id, label: label, type: 'number', 
        // The bound is published rather than only enforced by the clamp above, so a spinner, a screen
        // reader and an agent all read the same limit — but it is not relied on, since a browser
        // constrains the spinner and not what the author types.
        min: 1, max: totalPages, width: 'xs', value: draft, 
        // Selected on entry so the first digit typed replaces the page instead of appending to it —
        // `1` + `3` making `13`, which clamps to the last page and moves the option somewhere the
        // author never asked for. It takes both handlers and the click one is not redundant: `focus`
        // fires on *mousedown*, and the `mouseup` that finishes the click collapses the selection it
        // made. Measured in Chrome, focus-select alone still yields `13`, and deferring it a tick
        // still loses — a real click leaves tens of milliseconds between the two events. `click` runs
        // after `mouseup`, so it is what makes clicking in and typing replace; `focus` is what covers
        // arriving by Tab, which has no mouseup to undo it. (`select()` does work on a number input;
        // it is `selectionStart` and `setSelectionRange` that one does not support.)
        onFocus: (e) => {
            selectOnClickRef.current = true;
            e.currentTarget.select();
        }, 
        // Only the click that focused the field, so a second click into it places the caret and the
        // author can edit the page rather than having to retype it.
        onClick: (e) => {
            if (selectOnClickRef.current) {
                selectOnClickRef.current = false;
                e.currentTarget.select();
            }
        }, onChange: (e) => setDraft(e.target.value), onBlur: commit, onKeyDown: (e) => {
            if (e.key === 'Enter') {
                commit();
            }
        }, dataTestId: dataTestId }));
};
/**
 * Inspector body for a SINGLE_SELECT / MULTI_SELECT question. Renders inside
 * `ConsentPlacementInspector` (the 404px Figma chrome), so this panel is content-only: no nested
 * sidebar width, no second title/close. Option values are backend-only data keys and never shown to
 * the signer, so only the screen-reader label is per-locale.
 */
export const ConsentChoiceConfigPanel = () => {
    const translate = useLocalizeMessage();
    const { activeDocument, selectedElement } = useConsentStateContext();
    const { updateElement } = useConsentActionsContext();
    const documentRoles = useDocumentSignerRoles();
    // Stable React keys for the Configuration Data rows, independent of `ec__custom_data` (which has
    // no natural identity field). Tied to the element they were minted for, so switching to another
    // choice element does not carry one element's row identities onto another's rows.
    const customDataRowIdsRef = useRef(null);
    /**
     * The row focus is in, tagged with the element it belongs to. The one row the mark skips — see
     * `invalidRowIndex` below.
     */
    const [editingRow, setEditingRow] = useState(null);
    const panelRef = useRef(null);
    // Test id of the control focus has to land on once the reordered list has re-rendered, and the
    // text the live region announces alongside it. Both are set by a move and consumed on the next render.
    const pendingFocusRef = useRef(null);
    const [moveAnnouncement, setMoveAnnouncement] = useState('');
    // Configuration Data's Title is backend-only (never shown to the signer) and Cortex
    // requires it to be unique, so — matching the legacy consent editor (product/apps) —
    // a blank title is auto-filled with a generated ID as soon as the element is opened for
    // configuration, rather than staying empty. The guard makes this idempotent: once set,
    // it's persisted on the element and this never fires again for it.
    useEffect(() => {
        if (selectedElement && activeDocument && !selectedElement.config.ec__title) {
            updateElement(activeDocument.id, {
                ...selectedElement,
                config: { ...selectedElement.config, ec__title: generateUniqueId() },
            });
        }
    }, [selectedElement, activeDocument, updateElement]);
    // Deliberately unconditional: the move controls are the only thing that queues a focus target, and
    // it has to be applied on whichever render commits the reordered options.
    useEffect(() => {
        const testId = pendingFocusRef.current;
        if (!testId) {
            return;
        }
        pendingFocusRef.current = null;
        panelRef.current?.querySelector(`[data-testid="${testId}"]`)?.focus();
    });
    if (!selectedElement || !activeDocument) {
        return null;
    }
    const documentId = activeDocument.id;
    const element = selectedElement;
    const isSingle = element.type === 'SINGLE_SELECT';
    const preset = element.config.ec__choice_preset ?? 'custom';
    const isYnDefault = isSingle && preset === 'yn_default';
    const options = element.config.ec__options ?? [];
    const selectedRole = documentRoles.find((r) => r.value === element.config.ec__signer_role) ?? null;
    const hasNaOption = options.some((o) => o.key === NA_KEY);
    // Upper bound for each option's page. The editor writes the loaded PDF's own page count back onto
    // the document (`consent_pdf_editor.component.tsx`), so this is the authoritative count rather than
    // anything this panel could derive; a document whose PDF has not loaded yet reports 0, and falling
    // back to 1 keeps the current page selectable instead of bounding the field to nothing.
    const totalPages = Math.max(1, activeDocument.totalPages || 1);
    const updateConfig = (patch) => {
        updateElement(documentId, { ...element, config: { ...element.config, ...patch } });
    };
    const updateOptions = (next) => updateConfig({ ec__options: next });
    const nextAnchor = () => {
        const last = options[options.length - 1];
        const size = CONSENT_ELEMENT_TYPES[element.type];
        if (last) {
            return {
                page: last.page,
                xPct: last.position.xPct,
                yPct: Math.min(1 - size.defaultHeight, last.position.yPct + size.defaultHeight * 1.6),
            };
        }
        return { page: element.page, xPct: element.position.xPct, yPct: element.position.yPct };
    };
    const handleAddOption = () => {
        const nextKey = options.reduce((max, o) => Math.max(max, o.key), 0) + 1;
        const anchor = nextAnchor();
        const [created] = buildChoiceOptions([{ key: nextKey, value: '' }], anchor.page, {
            xPct: anchor.xPct,
            yPct: anchor.yPct,
        });
        updateOptions([...options, created]);
    };
    const handleAddNaOption = () => {
        const anchor = nextAnchor();
        const [created] = buildChoiceOptions([{ key: NA_KEY, value: YN_NA_OPTION_SPEC.value }], anchor.page, {
            xPct: anchor.xPct,
            yPct: anchor.yPct,
        });
        updateOptions([...options, created]);
    };
    const handleOptionValueChange = (id, value) => {
        updateOptions(options.map((o) => (o.id === id ? { ...o, value } : o)));
    };
    const handleOptionAriaLabelChange = (id, localizedAriaLabel) => {
        updateOptions(options.map((o) => (o.id === id ? { ...o, localizedAriaLabel } : o)));
    };
    // Already parsed and clamped by the field, which only commits an integer in range.
    const handleOptionPageCommit = (id, page) => {
        updateOptions(options.map((o) => (o.id === id ? { ...o, page } : o)));
    };
    const handleRemoveOption = (id) => {
        const remaining = options.filter((o) => o.id !== id);
        // Keep custom option keys contiguous; the locked Yes/No preset keeps its fixed keys.
        updateOptions(isYnDefault ? remaining : resequenceChoiceOptions(remaining));
    };
    // Moving an option renumbers the whole set, because a custom option's key is its position: the
    // keys stay 1..n in list order, so the number the author reads on a marker keeps matching the row
    // it was authored in. Only the custom set reorders — the Yes/No preset's order is part of the preset.
    const handleMoveOption = (index, offset) => {
        const target = index + offset;
        if (target < 0 || target >= options.length) {
            return;
        }
        const next = [...options];
        [next[index], next[target]] = [next[target], next[index]];
        updateOptions(resequenceChoiceOptions(next));
        // Focus has to travel with the option. The control that was just pressed is `disabled` once its
        // option reaches an end of the list, and a browser blurs a focused element the moment it is
        // disabled — leaving the author at the top of the document with nothing to Tab back from. Keys
        // are positions after resequencing, so the moved option's new controls are addressable already;
        // at an end, the opposite direction is the one still enabled.
        const movedKey = target + 1;
        const movedUp = offset === -1;
        const reachedEnd = movedUp ? target === 0 : target === options.length - 1;
        const direction = movedUp === reachedEnd ? 'down' : 'up';
        pendingFocusRef.current = `choice-option-move-${direction}-${movedKey}`;
        setMoveAnnouncement(translate('Option moved to position {position} of {total}', { position: movedKey, total: options.length }));
    };
    const handlePresetChange = (nextPreset) => {
        if (nextPreset === preset) {
            return;
        }
        if (nextPreset === 'yn_default') {
            const rebuilt = buildChoiceOptions(YN_DEFAULT_OPTION_SPECS.map((spec) => ({ ...spec })), element.page, { xPct: element.position.xPct, yPct: element.position.yPct });
            updateConfig({ ec__choice_preset: 'yn_default', ec__options: rebuilt });
            return;
        }
        updateConfig({ ec__choice_preset: 'custom' });
    };
    const customDataRows = element.config.ec__custom_data ?? [];
    // The card mints and then carries an id for every row it hands back, so the only ids this has to
    // supply are the ones for rows loaded from the element. `row-` keeps them clear of the card's own
    // `cd-` sequence. Discarded when the panel switches element, whose rows are a different list.
    const customDataRowIds = customDataRowIdsRef.current?.elementId === element.id ? customDataRowIdsRef.current.ids : [];
    const customDataItems = customDataRows.map((row, index) => ({
        id: customDataRowIds[index] ?? `row-${index}`,
        ...row,
    }));
    /**
     * Wholly derived from the rows, so every half-pair row the element carries is marked from the
     * moment the panel opens and stays marked until it is repaired or removed. That matters more here
     * than on the config form: choice elements get no gear icon, so this is the only Configuration
     * Data card they have, and the panel deliberately does not refuse — marking is its whole
     * enforcement, so anything that can suppress it is a row that silently goes unreported.
     *
     * The one exception is the row focus is in, which would otherwise complain about a pair the
     * author is halfway through typing. Holding *that* rather than holding the mark is what keeps the
     * two apart: nothing the author does to one row can take the mark off another. It is tagged with
     * its element because selecting another element swaps the panel's contents without remounting it,
     * so an untagged index would exempt whichever row happened to sit in that position next.
     */
    const exemptIndex = editingRow?.elementId === element.id ? editingRow.index : -1;
    const invalidRowIndex = customDataRows.findIndex((row, index) => index !== exemptIndex && isHalfPairCustomDataRow(row));
    const handleCustomDataRowsChange = (rows) => {
        customDataRowIdsRef.current = { elementId: element.id, ids: rows.map((row) => row.id) };
        updateConfig({ ec__custom_data: rows.map(({ key, value }) => ({ key, value })) });
        // Adding or removing shifts every index after it, the exempt one included, so it no longer
        // addresses the row the author is in.
        if (rows.length !== customDataRows.length) {
            setEditingRow(null);
        }
    };
    /**
     * The panel autosaves on keystroke and has no Save, so there is no boundary to refuse at: the
     * write that carries the row has already gone by the time any check could run, and withholding it
     * would hold every other edit on the template hostage to one row. Marking is all it can do, so
     * these two decide only which row to withhold the mark from, never whether to raise it. Moving
     * from Key to Value stays inside the row and reports neither. Neither moves focus: the author has
     * just put it somewhere themselves.
     */
    const handleCustomDataRowFocus = (index) => setEditingRow({ elementId: element.id, index });
    const handleCustomDataRowBlur = () => setEditingRow(null);
    // Only the optional N/A row can be removed in the locked Yes/No preset; Y and N are fixed.
    const canRemoveOption = (option) => !isYnDefault || option.key === NA_KEY;
    return (_jsxs("div", { ref: panelRef, className: 'flex min-h-0 w-full min-w-0 flex-col', "data-testid": 'consent-choice-config-panel', children: [_jsx("span", { role: 'status', className: 'nucleus-sr-only', "data-testid": 'choice-option-move-announcement', children: moveAnnouncement }), _jsxs("div", { className: 'flex min-w-0 w-full flex-col gap-6', children: [_jsx(Select, { label: translate('Signer role'), width: 'full', options: documentRoles, value: selectedRole ?? undefined, placeholder: translate('Select role...'), onChange: (option) => updateConfig({ ec__signer_role: option.value }), dataTestId: 'choice-panel-signer-role' }), documentRoles.length === 0 && (_jsx("p", { className: 'text-xs text-neutral-500', children: translate('Add roles on the Roles tab first.') })), isSingle && (_jsxs(RadioGroup, { label: translate('Response type'), value: preset, onChange: (value) => handlePresetChange(value), dataTestId: 'choice-panel-preset', children: [_jsx(RadioGroupOption, { option: { value: 'yn_default', label: translate('Yes / No (default)') } }), _jsx(RadioGroupOption, { option: { value: 'custom', label: translate('Custom') } })] })), _jsx(Input, { id: `choice-question-label-${element.id}`, label: translate('Label'), description: translate('For data purposes only. Not visible by the participant.'), width: 'full', value: element.config.ec__question_label ?? '', onChange: (e) => updateConfig({ ec__question_label: e.target.value }), dataTestId: 'choice-panel-question-label' }), _jsxs("div", { className: 'flex min-w-0 w-full flex-col gap-3', children: [isYnDefault && (_jsx(CalloutNotification, { type: 'info', title: translate('Yes / No response is fixed'), subtitle: translate('Keys and values cannot be edited. Optionally add a Not applicable (N/A) option. The signer only sees the radio button.') })), options.map((option, index) => (
                            // Grouped and named, because every row repeats the same two field labels — without it an
                            // author on a screen reader hears "Value" once per option with nothing to tell them apart.
                            // Number and move/remove sit in a header so Value / Screenreader / Page can use the full
                            // inspector width instead of sharing a column with the chrome.
                            _jsx("div", { role: 'group', "aria-label": translate('Option {key}', { key: String(option.key) }), className: 'flex min-w-0 w-full flex-col', "data-testid": `choice-option-row-${option.key}`, children: _jsx(Card, { className: 'w-full min-w-0', children: _jsxs(Card.Content, { className: 'flex min-w-0 flex-col gap-4', children: [(!isYnDefault || canRemoveOption(option)) && (_jsxs("div", { className: 'flex min-w-0 items-center gap-2', children: [!isYnDefault && _jsx("span", { className: 'text-sm font-medium text-neutral-500', children: option.key }), _jsxs("div", { className: 'ml-auto flex shrink-0 items-center', children: [!isYnDefault && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'transparent', size: 'sm', label: translate('Move up'), content: _jsx(Icon, { name: 'chevronUp', size: 'sm' }), disabled: index === 0, onClick: () => handleMoveOption(index, -1), dataTestId: `choice-option-move-up-${option.key}` }), _jsx(Button, { variant: 'transparent', size: 'sm', label: translate('Move down'), content: _jsx(Icon, { name: 'chevronDown', size: 'sm' }), disabled: index === options.length - 1, onClick: () => handleMoveOption(index, 1), dataTestId: `choice-option-move-down-${option.key}` })] })), canRemoveOption(option) && (_jsx(Button, { variant: 'transparent', size: 'sm', label: translate('Remove'), content: _jsx(Icon, { name: 'delete', size: 'sm' }), onClick: () => handleRemoveOption(option.id), dataTestId: `choice-option-remove-${option.key}` }))] })] })), _jsx(Input, { id: `choice-option-${element.id}-${option.key}`, label: translate('Value'), width: 'full', value: option.value, disabled: isYnDefault, onChange: (e) => handleOptionValueChange(option.id, e.target.value), dataTestId: `choice-option-input-${option.key}` }), _jsx(Input, { id: `choice-option-aria-${element.id}-${option.key}`, label: translate('Screenreader label'), description: translate('Label must be in the language of the document'), required: true, width: 'full', value: option.localizedAriaLabel ?? '', placeholder: translate('Enter a value in the language of the document'), onChange: (e) => handleOptionAriaLabelChange(option.id, e.target.value), dataTestId: `choice-option-aria-input-${option.key}` }), _jsx(ChoiceOptionPageField, { id: `choice-option-page-${element.id}-${option.key}`, label: translate('Page'), page: option.page, totalPages: totalPages, onCommit: (page) => handleOptionPageCommit(option.id, page), dataTestId: `choice-option-page-input-${option.key}` })] }) }) }, option.id))), isYnDefault ? (!hasNaOption && (_jsx(Button, { variant: 'transparent', size: 'sm', label: `+ ${translate('Add Not applicable (N/A)')}`, onClick: handleAddNaOption, dataTestId: 'choice-add-na-option' }))) : (_jsx(Button, { variant: 'transparent', size: 'sm', label: `+ ${translate('Add option')}`, onClick: handleAddOption, dataTestId: 'choice-add-option' }))] }), _jsx(Checkbox, { id: `choice-optional-${element.id}`, label: translate('Optional'), description: translate('The signer can complete their part without answering this question.'), checked: element.config.ec__optional ?? false, onChange: (checked) => updateConfig({ ec__optional: checked }), dataTestId: 'choice-panel-optional' }), _jsx(ConsentConfigurationData, { idPrefix: element.id, title: element.config.ec__title ?? '', description: element.config.ec__description ?? '', rows: customDataItems, onTitleChange: (value) => updateConfig({ ec__title: value }), onDescriptionChange: (value) => updateConfig({ ec__description: value }), onRowsChange: handleCustomDataRowsChange, invalidRowIndex: invalidRowIndex, onRowFocus: handleCustomDataRowFocus, onRowBlur: handleCustomDataRowBlur })] })] }));
};
